import { z } from 'zod';
import { MOWER_IDS, type MowerId } from '../../content/mowers';
import { isValidName } from '../../content/names';
import { TOOLS, type ToolId } from '../../content/tools';
import { ENDLESS_MODES, RACE_SIZES, type EndlessMode } from '../../gen/modes';
import type { SimStatus } from '../../sim/simulation';
import { UpgradeLevelsSchema, type UpgradeLevels } from '../save/schema';
import { MAX_LAWN_INPUTS, MAX_LAWN_TICKS, MAX_RUN_LAWNS, type PostedRun } from './run';

/**
 * The online API's contract (docs/ONLINE.md, "API (v1)"), shared by the game and the server so
 * the two cannot drift. Requests are strict: the server refuses fields it does not know. Responses
 * are lenient: an older game ignores fields a newer server adds.
 */

export const API_BASE = '/api/v1';
/** Headers every request carries, so the server can answer "update the game" (HTTP 426). */
export const APP_VERSION_HEADER = 'X-Lawnmower-App';
export const RULES_VERSION_HEADER = 'X-Lawnmower-Rules';

const int = () => z.number().int();
const MowerIdSchema = z.enum(MOWER_IDS as [MowerId, ...MowerId[]]);
const ToolIdSchema = z.enum(TOOLS.map((t) => t.id) as [ToolId, ...ToolId[]]);
const EndlessModeSchema = z.enum(ENDLESS_MODES.map((m) => m.id) as [EndlessMode, ...EndlessMode[]]);
const SIM_STATUSES = [
  'running',
  'won',
  'broken',
  'outOfFuel',
  'caught',
  'outmowed',
  'late',
] as const satisfies readonly SimStatus[];
type UpgradeKey = keyof UpgradeLevels;
const UPGRADE_KEYS = Object.keys(UpgradeLevelsSchema.shape) as [UpgradeKey, ...UpgradeKey[]];

// ── Runs ─────────────────────────────────────────────────────────────────────────────────────

export const RunBoardSchema = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('daily'), date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/) }),
  z.strictObject({ kind: z.literal('campaign'), lawn: int().min(1).max(999) }),
  z.strictObject({
    kind: z.literal('endless'),
    mode: EndlessModeSchema,
    seed: z.string().min(1).max(64),
    firstLawnFor: MowerIdSchema.optional(),
  }),
  z.strictObject({
    kind: z.literal('race'),
    seed: z.string().min(1).max(64),
    size: z.enum(RACE_SIZES),
  }),
  z.strictObject({
    kind: z.literal('community'),
    id: z.string().regex(/^[0-9a-z]{10}$/),
    code: z.string().min(10).max(2000),
  }),
]);

export const PostedRunSchema = z.strictObject({
  rules: int().min(1),
  board: RunBoardSchema,
  mower: MowerIdSchema,
  loadout: z
    .strictObject({
      upgrades: z.partialRecord(z.enum(UPGRADE_KEYS), int().min(0).max(20)).optional(),
      tools: z
        .array(z.tuple([ToolIdSchema, int().min(0).max(20)]))
        .max(4)
        .optional(),
    })
    .optional(),
  paint: z.string().max(48).optional(),
  lawns: z
    .array(
      z.strictObject({
        endTick: int().min(0).max(MAX_LAWN_TICKS),
        inputs: z.array(z.tuple([int().min(0), int().min(0).max(99)])).max(MAX_LAWN_INPUTS),
      }),
    )
    .min(1)
    .max(MAX_RUN_LAWNS),
  claimed: z.strictObject({
    ticks: int().min(0),
    score: int().min(0),
    status: z.enum(SIM_STATUSES),
  }),
});

/** Every posted run the schema accepts is a {@link PostedRun}, and the other way round. */
type Same<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false;
export const POSTED_RUN_SCHEMA_MATCHES: Same<z.infer<typeof PostedRunSchema>, PostedRun> = true;

const BoardResultSchema = z.object({
  board: z.string(),
  scope: z.string(),
  rank: int().min(1),
  total: int().min(1),
  /** "Top 12%" (topPercent). */
  top: int().min(1).max(100),
  /** A new personal best on this board. */
  best: z.boolean(),
  /**
   * The player's time equals the board's best and others share it: how many players have it, and
   * whether this player posted it first (they stay #1; docs/DAILY_ITEMS.md). Absent otherwise,
   * and from servers before 2026-10-28.
   */
  tied: z.object({ count: int().min(2), first: z.boolean() }).optional(),
});

/** POST /runs: ranked (on these boards; none when it did not qualify), still checking, or refused. */
export const PostRunResponseSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('ranked'), boards: z.array(BoardResultSchema) }),
  z.object({ status: z.literal('checking'), id: z.string() }),
  z.object({ status: z.literal('refused'), problem: z.string() }),
]);
export type PostRunResponse = z.infer<typeof PostRunResponseSchema>;

// ── Players ──────────────────────────────────────────────────────────────────────────────────

/** Public player IDs: 10 characters of lowercase base 32. */
export const PlayerIdSchema = z.string().regex(/^[0-9a-z]{10}$/);
/** The secret: 32 random bytes, base64url. Sent as `Authorization: Bearer`; never shown. */
export const PlayerTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/);

/**
 * A display name, tagged by kind so free-typed names can come later without migrating (owner,
 * 2026-10-24). `words`: two picks from the word lists in src/content/names.ts by fixed index, and
 * a number. Shape only, so a board still reads once a word is retired; a name being chosen is
 * checked against the lists ({@link ChosenNameSchema}).
 */
export const PlayerNameSchema = z.discriminatedUnion('kind', [
  z.strictObject({
    kind: z.literal('words'),
    parts: z.tuple([int().min(0), int().min(0)]),
    number: int().min(0).max(99),
  }),
]);
export type PlayerName = z.infer<typeof PlayerNameSchema>;

/** A name a player may choose: every word still in the lists, the number allowed. */
export const ChosenNameSchema = PlayerNameSchema.refine(isValidName, 'not a name the game makes');

/** POST /players: a new player. */
export const JoinResponseSchema = z.object({
  id: PlayerIdSchema,
  token: PlayerTokenSchema,
  name: PlayerNameSchema,
});

/** PATCH /me: rename. */
export const RenameRequestSchema = z.strictObject({ name: ChosenNameSchema });

// ── Boards ───────────────────────────────────────────────────────────────────────────────────

export const BoardRowSchema = z.object({
  rank: int().min(1),
  player: PlayerIdSchema,
  name: PlayerNameSchema,
  value: int(),
  /** When it was set (ms since 1970, UTC). */
  at: int(),
  /** They won last week's featured board (a crown by their name). */
  crown: z.boolean().optional(),
});

/** GET /boards/:board/:scope: the top 10, the rows around the player, and the player's own. */
export const BoardResponseSchema = z.object({
  board: z.string(),
  scope: z.string(),
  total: int().min(0),
  top: z.array(BoardRowSchema).max(10),
  around: z.array(BoardRowSchema).max(11),
  me: BoardRowSchema.nullable(),
  /** When the server answered, for "updated 5 min ago". */
  at: int(),
});
export type BoardResponse = z.infer<typeof BoardResponseSchema>;

/**
 * GET /ghosts/:board/:scope?rules=N: the best run on a board that was played on these rules and not
 * flagged, to race as a ghost (docs/ONLINE.md). The run is only shape-checked here; the game replays
 * it with its own rules, which match by the query.
 */
export const GhostResponseSchema = z.object({
  name: PlayerNameSchema,
  value: int(),
  run: z.custom<PostedRun>(
    (v) =>
      typeof v === 'object' &&
      v !== null &&
      Array.isArray((v as { lawns?: unknown }).lawns) &&
      typeof (v as { mower?: unknown }).mower === 'string',
  ),
});
export type GhostResponse = z.infer<typeof GhostResponseSchema>;

// ── Challenges ───────────────────────────────────────────────────────────────────────────────

/** How long a friend has to answer a challenge (owner, 2026-10-25). */
export const CHALLENGE_DAYS = 7;

/** POST /challenges and POST /challenges/:id/answers: a cleared Daily. */
export const ChallengeRunSchema = z.strictObject({ run: PostedRunSchema });

const AnswerSchema = z.object({
  player: PlayerIdSchema,
  name: PlayerNameSchema,
  ticks: int(),
  at: int(),
});

/**
 * GET /challenges/:id: who sent it, their time and run (to race as a ghost), when it closes, and
 * every friend's best answer so far.
 */
export const ChallengeSchema = z.object({
  id: z.string(),
  date: z.string(),
  from: z.object({ player: PlayerIdSchema, name: PlayerNameSchema }),
  ticks: int(),
  run: GhostResponseSchema.shape.run,
  closes: int(),
  answers: z.array(AnswerSchema),
});
export type Challenge = z.infer<typeof ChallengeSchema>;

/** POST /challenges: the new challenge's id and the link to share. */
export const ChallengeCreatedSchema = z.object({ id: z.string(), url: z.string() });

/** GET /challenges: the player's own challenges and the ones they answered, newest first. */
export const ChallengeListSchema = z.object({
  challenges: z.array(ChallengeSchema.omit({ run: true })),
});
export type ChallengeList = z.infer<typeof ChallengeListSchema>;

// -- Community lawns ---------------------------------------------------------------------------

/**
 * Reports from different players that hide a community lawn until the owner reviews it (owner,
 * 2026-10-25: 2, changed from 3).
 */
export const LAWN_REPORTS_TO_HIDE = 2;
/** Community lawns a player may have up at once. */
export const MAX_COMMUNITY_LAWNS = 30;

/** POST /lawns: a lawn's code (src/meta/lawnCode.ts). */
export const PostLawnRequestSchema = z.strictObject({ code: z.string().min(10).max(2000) });

export const CommunityLawnSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  biome: z.string(),
  by: z.object({ player: PlayerIdSchema, name: PlayerNameSchema }),
  /**
   * Likes, and whether the asking player likes it. The game stopped showing likes (owner,
   * 2026-10-28; plays show what is popular); the server keeps answering them for 1.1 and 1.2.
   */
  likes: int().min(0),
  /** How many players have played it (each counted once). */
  plays: int().min(0),
  liked: z.boolean(),
  /** The asking player made it (false without a token). */
  mine: z.boolean(),
  at: int(),
});
export type CommunityLawn = z.infer<typeof CommunityLawnSchema>;

/** GET /lawns: a page of lawns, and the cursor for the next (null at the end). */
export const CommunityListSchema = z.object({
  lawns: z.array(CommunityLawnSchema),
  next: z.string().nullable(),
});
export type CommunityList = z.infer<typeof CommunityListSchema>;

// -- The owner's review of reported lawns ----------------------------------------------------------

/** POST /admin/lawns/:id: keep or remove a reported lawn; optionally hide or unhide its maker. */
export const ReviewRequestSchema = z.strictObject({
  action: z.enum(['keep', 'remove']),
  hideMaker: z.boolean().optional(),
});

export const ReviewLawnSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  biome: z.string(),
  /** 0 shown, 1 hidden by reports, 2 removed, 3 reviewed and kept. */
  status: int(),
  by: z.object({ player: PlayerIdSchema, name: PlayerNameSchema, hidden: z.boolean() }),
  reports: int().min(0),
  lastReport: int().nullable(),
  likes: int().min(0),
  plays: int().min(0),
  at: int(),
  reviewedAt: int().nullable(),
});
export type ReviewLawn = z.infer<typeof ReviewLawnSchema>;

/** GET /admin/lawns: lawns waiting for a look, and the latest reviewed (to change a decision). */
export const ReviewListSchema = z.object({
  waiting: z.array(ReviewLawnSchema),
  reviewed: z.array(ReviewLawnSchema),
});
export type ReviewList = z.infer<typeof ReviewListSchema>;

// -- Blocking ------------------------------------------------------------------------------------

/** POST /blocks: the player to block. */
export const BlockRequestSchema = z.strictObject({ player: PlayerIdSchema });

/** GET /blocks: the players this player blocked. */
export const BlockListSchema = z.object({
  blocked: z.array(z.object({ player: PlayerIdSchema, name: PlayerNameSchema, at: int() })),
});
export type BlockList = z.infer<typeof BlockListSchema>;

// -- Friends -------------------------------------------------------------------------------------

/** Friends a player may have. */
export const MAX_FRIENDS = 200;

/**
 * Friend codes (docs/FRIENDS.md, step 3): 8 characters from the room codes' alphabet, secret (not
 * the player id, which boards show). Shown `K7QX-2M9P`.
 */
export const FriendCodeSchema = z.string().regex(/^[2-9A-HJKMNP-Z]{8}$/);
export const FRIEND_CODE_LENGTH = 8;

/**
 * POST /friends: by friend code (shared, so friends both ways at once), or by player id (after a
 * race, or a link from before friend codes): then a request the other player accepts, unless they
 * asked first. Answers the player, `pending` while a request waits.
 */
export const FriendRequestSchema = z.union([
  z.strictObject({ code: FriendCodeSchema }),
  z.strictObject({ player: PlayerIdSchema }),
]);

/** The answer to POST /friends. */
export const AddFriendAnswerSchema = z.object({
  player: PlayerIdSchema,
  name: PlayerNameSchema,
  pending: z.boolean().optional(),
});

/** GET /me/friend-code: the player's own secret friend code. */
export const FriendCodeAnswerSchema = z.object({ code: FriendCodeSchema });

/** Friend requests waiting for the player to answer (GET /inbox). */
export const FriendRequestRowSchema = z.object({
  player: PlayerIdSchema,
  name: PlayerNameSchema,
  at: int(),
});
export type FriendRequestRow = z.infer<typeof FriendRequestRowSchema>;

/** The most requests a player may have waiting on others at once. */
export const MAX_PENDING_REQUESTS = 20;
/** A declined request cannot be sent again for this long. */
export const DECLINED_MS = 30 * 24 * 60 * 60 * 1000;

/** GET /friends: the player's friends, newest first. */
export const FriendListSchema = z.object({
  friends: z.array(
    z.object({
      player: PlayerIdSchema,
      name: PlayerNameSchema,
      at: int(),
      /** The friend has the game open now (docs/FRIENDS.md); absent from older servers. */
      online: z.boolean().optional(),
      /** The farthest campaign lawn on their lawn boards (null: none yet; absent: older server). */
      lawn: int().min(1).nullable().optional(),
    }),
  ),
});
export type FriendList = z.infer<typeof FriendListSchema>;

/**
 * A player's numbers for a friend's card (docs/FRIENDS.md, "The friend's card"), all from the
 * boards they post to: the farthest campaign lawn, today's Daily (time and rank), the Daily
 * streaks, and the best Endless Lawns score of all time. Null: nothing there yet.
 */
export const FriendStatsSchema = z.object({
  lawn: int().min(1).nullable(),
  daily: z.object({ ticks: int().min(0), rank: int().min(1) }).nullable(),
  streak: z.object({ current: int().min(0), longest: int().min(0) }).nullable(),
  endless: int().min(0).nullable(),
});
export type FriendStats = z.infer<typeof FriendStatsSchema>;

/** GET /friends/:player?daily=<scope>: the player's numbers beside a friend's. */
export const FriendCardSchema = z.object({ you: FriendStatsSchema, them: FriendStatsSchema });
export type FriendCard = z.infer<typeof FriendCardSchema>;

/** A Daily board's scope, as a friend's card asks for it (`date:lawnKey`). */
export const DailyScopeSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}:[0-9A-Za-z_-]{1,64}$/);

/** GET /players/:id: a player's name (to ask "add them as a friend?"). */
export const PlayerInfoSchema = z.object({ player: PlayerIdSchema, name: PlayerNameSchema });
export type PlayerInfo = z.infer<typeof PlayerInfoSchema>;

// -- The featured board and holding bonuses ---------------------------------------------------------

/** GET /featured: this week's featured campaign lawn, its board and scope, and when it ends. */
export const FeaturedSchema = z.object({
  week: z.string(),
  lawn: int().min(1),
  board: z.string(),
  scope: z.string(),
  /** When the week ends (ms since 1970, UTC). */
  ends: int(),
});
export type Featured = z.infer<typeof FeaturedSchema>;

/**
 * GET /me/awards: the holding bonuses earned over the last days and weeks (src/meta/online/awards.ts).
 * `kind` is any string, so a game ignores kinds a newer server adds.
 */
export const AwardListSchema = z.object({
  awards: z.array(z.object({ id: z.string().max(64), kind: z.string() })),
});
export type AwardList = z.infer<typeof AwardListSchema>;

/** Any error: what went wrong, and whether updating the game would help. */
export const ErrorResponseSchema = z.object({
  error: z.string(),
  update: z.boolean().optional(),
});
